import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDrag, useDrop } from "react-dnd";
import './matchMaking.css';
import { DownloadOutlined } from '@mui/icons-material';
import MatchAudioPng from "utils/assets/images/Match-Audio.png";
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import Loading from 'components/Loading';
import styled from 'styled-components';
import { useCurrentUser } from "store/users/selectors";

const ItemTypes = {
    MATCH_ITEM: "match_item",
};

const DropdownContainer = styled.div`
  position: relative;
  z-index: 99999;
`;

const DropdownToggle = styled.div`
  cursor: pointer;
`;

const DropdownMenu = styled.div`
  position: absolute;
//   top: 100%;
//   right: auto;
  left:-65px; 
  margin-top: 4px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 99999;
  max-width: 145px;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const shuffleArray = (array) => {
    let shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
};

const MatchItem = ({ id, image, isSelected, MatchType, text, item, playingAudio, onAudioPlay, onRegisterMediaElement, onReset, disabled = false }) => {
    const [audio] = useState(new Audio(MatchType === 'ImageAndAudio' ? item?.match_audio : item?.match_item));

    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { downloadAudio, downloadTranscipt, districtPermissionConfig } = usePermissionForUser();
    const { audio_download = false, audio_transcript_download = false } = districtPermissionConfig;

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: ItemTypes.MATCH_ITEM,
        item: () => ({ id, image, text, MatchType }),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        options: {
            touchSlop: 1,
            dragOffsetThreshold: 0,
        },
        canDrag: () => !disabled,
    }));

    useEffect(() => {
        const emptyImage = new Image();
        emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
        preview(emptyImage, { captureDraggingState: true });
    }, [preview]);

    const onPlayAudio = () => {
        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.currentTime = 0;
            onAudioPlay(audio);
            audio.play().catch(() => {
                setIsPlaying(false);
            });
            setIsPlaying(true);
        }
    };

    const handleDownloadAudio = async () => {
        setIsLoading(true);
        setIsDropdownOpen(false);
        const audioUrl = MatchType === "ImageAndAudio" ? item?.match_audio : item?.match_item;
        await downloadAudio({ audioUrl });
        setIsLoading(false);
    };

    const handleDownloadTranscript = async () => {
        setIsLoading(true);
        setIsDropdownOpen(false);
        const audioUrl = MatchType === "ImageAndAudio" ? item?.match_audio : item?.match_item;
        await downloadTranscipt({ audioUrl });
        setIsLoading(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleAudioEnd = () => setIsPlaying(false);
        const handlePause = () => setIsPlaying(false);
        audio.addEventListener('ended', handleAudioEnd);
        audio.addEventListener('pause', handlePause);
        return () => {
            audio.removeEventListener('ended', handleAudioEnd);
            audio.removeEventListener('pause', handlePause);
        };
    }, [audio]);

    useEffect(() => {
        if (playingAudio == null || playingAudio === audio) {
            return;
        }
        if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
        }
        setIsPlaying(false);
    }, [playingAudio, audio]);

    useEffect(() => {
        if (typeof onRegisterMediaElement !== 'function' || !audio) {
            return;
        }
        onRegisterMediaElement(audio);
    }, [audio, onRegisterMediaElement]);

    return (
        <div
            ref={drag}
            className="MatchItemInerCstm"
            style={{
                display: "flex",
                marginTop: "20px",
                flexWrap: "wrap",
                opacity: isDragging ? 0.5 : isSelected ? 0.5 : 1,
                marginRight: "20px",
                cursor: isSelected ? "pointer" : "grab",
                userSelect: "none",
                WebkitUserSelect: "none",
                border: "1px solid gray",
                borderRadius: "5px",
                padding: "5px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
            onClick={() => {
                if (isSelected) {
                    if (onReset) onReset(id);
                    return;
                }
                if (MatchType === "Audio" || MatchType === "ImageAndAudio") {
                    onPlayAudio();
                }
            }}
        >
            <div>
                <div
                    style={{
                        borderRadius: "0px !important",
                        minWidth: '20px',
                        marginRight: '5px',
                        maxHeight: '20px',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    className={`matchItemIndex`}
                >
                    {String.fromCharCode(65 + id)}
                </div>
                {MatchType === "ImageAndAudio" && (
                    <div
                        style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPlayAudio();
                        }}
                    >
                        <img
                            onClick={(e) => {
                                e.stopPropagation();
                                onPlayAudio();
                            }}
                            src={MatchAudioPng}
                            alt={"audio"}
                            style={{
                                width: "23px",
                                height: "23px",
                                objectFit: "cover",
                                borderRadius: "50%",
                                cursor: "pointer",
                            }}
                        />
                        {(audio_download || audio_transcript_download) && (
                            <DropdownContainer ref={dropdownRef}>
                                <DropdownToggle
                                    className="flex items-center gap-5px"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    {isLoading && <Loading size={16} />}
                                    <DownloadOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
                                </DropdownToggle>

                                {isDropdownOpen && (
                                    <DropdownMenu>
                                        {audio_download && (
                                            <DropdownItem onClick={handleDownloadAudio}>Download Audio</DropdownItem>
                                        )}
                                        {audio_transcript_download && (
                                            <DropdownItem onClick={handleDownloadTranscript}>
                                                Download Transcript
                                            </DropdownItem>
                                        )}
                                    </DropdownMenu>
                                )}
                            </DropdownContainer>
                        )}
                    </div>
                )}
            </div>
            {MatchType === "Text" ? (
                <div><span dangerouslySetInnerHTML={{ __html: text || '' }} /></div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center" }}>
                    <img
                        onClick={(e) => {
                            e.stopPropagation();
                            if (MatchType === "Audio") {
                                onPlayAudio();
                            }
                        }}
                        src={
                            MatchType === "Audio"
                                ? MatchAudioPng
                                : image
                        }
                        alt={`Match ${id}`}
                        draggable={false}
                        style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            cursor: "pointer",
                        }}
                    />
                    {MatchType === "Audio" && (audio_download || audio_transcript_download) && (
                        <DropdownContainer ref={dropdownRef}>
                            <DropdownToggle
                                className="flex items-center gap-2px "
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                                {isLoading && <Loading size={16} />}
                                <DownloadOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
                            </DropdownToggle>

                            {isDropdownOpen && (
                                <DropdownMenu>
                                    {audio_download && (
                                        <DropdownItem onClick={handleDownloadAudio}>Download Audio  </DropdownItem>
                                    )}
                                    {audio_transcript_download && (
                                        <DropdownItem onClick={handleDownloadTranscript}>
                                            Download Transcript
                                        </DropdownItem>
                                    )}
                                </DropdownMenu>
                            )}
                        </DropdownContainer>
                    )}
                </div>
            )}
        </div>
    );
};

const DropTarget = ({ id, onDrop, selectedItem, values, QuestionType, onAudioPlay, playingAudio, onRegisterMediaElement, disabled = false, isAiAnsewr = false}) => {
    const [audio, setAudio] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        const audioUrl =
            QuestionType === "Audio" || QuestionType === "ImageAndAudio"
                ? (values?.questionAudio || values?.match_audio || null)
                : null;

        if (!audioUrl) {
            setAudio(null);
            return;
        }

        setAudio(new Audio(audioUrl));
    }, [QuestionType, values?.questionAudio, values?.match_audio]);

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.MATCH_ITEM,
        drop: (item) => onDrop && onDrop(item.id, id),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
        canDrop: () => !disabled,
    }));

    useEffect(() => {
        if (!audio) return;
        const handleAudioEnd = () => setIsPlaying(false);
        const handlePause = () => setIsPlaying(false);
        audio.addEventListener('ended', handleAudioEnd);
        audio.addEventListener('pause', handlePause);
        return () => {
            audio.removeEventListener('ended', handleAudioEnd);
            audio.removeEventListener('pause', handlePause);
            audio.pause();
            audio.currentTime = 0;
        };
    }, [audio]);

    useEffect(() => {
        if (!audio || playingAudio == null || playingAudio === audio) {
            return;
        }
        if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
        }
        setIsPlaying(false);
    }, [playingAudio, audio]);

    useEffect(() => {
        if (typeof onRegisterMediaElement !== 'function' || !audio) {
            return;
        }
        onRegisterMediaElement(audio);
    }, [audio, onRegisterMediaElement]);

    const onPlayQuestionAudio = () => {
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            audio.currentTime = 0;
            setIsPlaying(false);
        } else {
            audio.pause();
            audio.currentTime = 0;
            onAudioPlay(audio);
            audio.play().catch(() => {
                setIsPlaying(false);
            });
            setIsPlaying(true);
        }
    };

    const handlePlayAudio = () => {
        if (QuestionType === "Audio" || QuestionType === "ImageAndAudio") {
            onPlayQuestionAudio();
        }
    };

    return (
        <div
            ref={drop}
            style={{
                borderRadius: "0px !important",
                minWidth: "100px",
                minHeight: "36px",
                marginRight: "5px",
                marginBottom: "15px",
                color: "white",
                backgroundColor: isOver && canDrop ? "#324b8c" : "#010e4c",
                cursor: (QuestionType === "Audio" || QuestionType === "ImageAndAudio") ? "pointer" : "default",
                border: isOver && canDrop ? "1px dotted #000" : undefined,
                transition: "background-color 0.3s, border 0.3s",
            }}
            className={`111 matchItem matchItemTxtInr`}
            onClick={handlePlayAudio}
        >
            <div>
                {selectedItem !== undefined  && (
                    <div
                        style={{
                            borderRadius: "0px !important",
                            minWidth: isAiAnsewr ? "0px" : "18px",
                            marginRight: '5px',
                            maxHeight: '20px',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        className={`matchItemIndex`}
                    >
                        {isAiAnsewr ? null: String.fromCharCode(65 + selectedItem)}
                    </div>
                )}
            </div>
            <div className="match-making-custom">
                {
                    QuestionType === 'Audio' ? <img src={MatchAudioPng} style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="audio" /> : QuestionType === 'Image' ? <img src={values?.questionImage} style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="image" /> : QuestionType === 'ImageAndAudio' ? <img src={values?.match_item} style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="image" /> : (
                        <div style={{
                            lineHeight: '1.6',
                            wordBreak: 'break-word'
                        }}>
                            <style>{`
                          .match-making-custom div ol, div ul {
                            padding-left: 30px;
                            margin: 10px 0;
                          }
                          .match-making-custom div ol {
                            list-style-type: decimal;
                          }
                          .match-making-custom div ul {
                            list-style-type: disc;
                          }
                         .match-making-custom div li {
                            margin: 5px 0;
                            line-height: 1.5;
                          }
                          .match-making-custom div p {
                            margin: 10px 0;
                          }
                        `}</style>
                            <span dangerouslySetInnerHTML={{ __html: values?.item || '' }} />
                        </div>
                    )
                }
            </div>
        </div>
    );
};

const matchDataFromSelected = (selectedItems) =>
    Object.entries(selectedItems).map(([targetId, itemId]) => ({
        item: parseInt(targetId, 10),
        match_item: itemId,
    }));

const MatchMakingPreview = ({ question, value, onChange, from, onRegisterMediaElement, onMediaPlay, isAiAnsewr }) => {
    const [selectedItems, setSelectedItems] = useState({});
    const [shuffledItems, setShuffledItems] = useState([]);
    const [matchItems, setMatchItems] = useState([]);
    const MatchType = question?.matchType || 'Image';
    const QuestionType = question?.questionType || 'Text';
    const [currentAudio, setCurrentAudio] = useState(null);
    const currentAudioRef = useRef(null);
    const selectedAnswer = value;
    const currentUser = useCurrentUser();

    const handleAudioPlay = useCallback(
        (newAudio) => {
            if (!newAudio) {
                return;
            }
            const prev = currentAudioRef.current;
            if (prev && prev !== newAudio) {
                try {
                    prev.pause();
                    prev.currentTime = 0;
                } catch (e) {}
            }
            currentAudioRef.current = newAudio;
            setCurrentAudio(newAudio);
            if (typeof onMediaPlay === 'function') {
                onMediaPlay(newAudio);
            }
        },
        [onMediaPlay],
    );

    const handleDrop = (itemId, targetId) => {
        if (from === "shareAssignment" && currentUser?.role === "student") return;
        if (isAiAnsewr) return;
        setSelectedItems((prev) => {
            const next = { ...prev };
            const existingTarget = Object.entries(next).find(([, v]) => v === itemId)?.[0];
            if (existingTarget) delete next[existingTarget];
            next[targetId] = itemId;
            if (typeof onChange === 'function') onChange(matchDataFromSelected(next));
            return next;
        });
    };

    const handleReset = (itemId) => {
        if (from === "shareAssignment" && currentUser?.role === "student") return;
        if (isAiAnsewr) return;
        setSelectedItems((prev) => {
            const next = {};
            Object.keys(prev).forEach((k) => {
                if (prev[k] !== itemId) next[k] = prev[k];
            });
            if (typeof onChange === 'function') onChange(matchDataFromSelected(next));
            return next;
        });
    };

    useEffect(() => {
        if (selectedAnswer?.match_data) {
            const preselectedItems = {};
            selectedAnswer.match_data.forEach(({ item, match_item }) => {
                preselectedItems[item] = match_item;
            });
            setSelectedItems(preselectedItems);
        }
        else if (isAiAnsewr && question?.options?.length) {
            const correct = {};
            question.options.forEach((_, idx) => {
                correct[idx] = idx;
            });
            setSelectedItems(correct);
        }
    }, [selectedAnswer, isAiAnsewr, question]);

    useEffect(() => {
        const shuffledOptions = question.options.map((option, index) => ({
            ...option,
            oldIndex: index, // Store the original index for correctness check
        }));
        setMatchItems(question.options); // Store the original items (used for the teacher)
        setShuffledItems(shuffledOptions); // Use shuffled items for the DropTarget display
    }, [question]);


    useEffect(() => {
        return () => {
            const a = currentAudioRef.current;
            if (a) {
                try {
                    a.pause();
                    a.currentTime = 0;
                } catch (e) {}
                currentAudioRef.current = null;
            }
        };
    }, []);

    return (
        <div>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
                {shuffledItems.map((option, index) => {
                    const oldIndex = option?.oldIndex;
                    return (
                        <DropTarget
                            key={oldIndex}
                            id={oldIndex}
                            selectedItem={selectedItems[oldIndex]}
                            values={option}
                            QuestionType={QuestionType}
                            onAudioPlay={handleAudioPlay}
                            playingAudio={currentAudio}
                            onRegisterMediaElement={onRegisterMediaElement}
                            onDrop={handleDrop}
                            disabled={(from === "shareAssignment" && currentUser?.role === "student") || Boolean(isAiAnsewr)}
                            isAiAnsewr={isAiAnsewr}
                        />
                    );
                })}
            </div>
            <div className="match-item match-item-preview">
                {matchItems.map((item, index) => (
                    <MatchItem
                        key={index}
                        id={index}
                        image={item?.match_item}
                        text={item?.match_text}
                        item={item}
                        isSelected={Object.values(selectedItems).includes(index)}
                        MatchType={MatchType}
                        playingAudio={currentAudio}
                        onAudioPlay={handleAudioPlay}
                        onRegisterMediaElement={onRegisterMediaElement}
                        onReset={handleReset}
                        disabled={(from === "shareAssignment" && currentUser?.role === "student") || Boolean(isAiAnsewr)}
                    />
                ))}
            </div>
            {isAiAnsewr ? 
            <>
            <label>Correct Answer:- </label>
            <div className="match-item match-item-preview">
                {shuffledItems.map((option, index) => {
                    const oldIndex = option?.oldIndex;
                    return (
                        <DropTarget
                            key={oldIndex}
                            id={oldIndex}
                            selectedItem={selectedItems[oldIndex]}
                            values={option}
                            QuestionType={QuestionType}
                            onAudioPlay={handleAudioPlay}
                            playingAudio={currentAudio}
                            onRegisterMediaElement={onRegisterMediaElement}
                            onDrop={handleDrop}
                            disabled={(from === "shareAssignment" && currentUser?.role === "student") || Boolean(isAiAnsewr)}
                        />
                    );
                })}
            </div>
                
            
            </>
                
                
                : null}
        </div>
    );
};

export default MatchMakingPreview;
